import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Camera, Play, Facebook, Instagram, Twitter, Youtube, Menu, X, Mail, MapPin, Phone, 
  Film, Clapperboard, MonitorPlay, XCircle, Linkedin, Globe, Send, Calendar, User, Tag, 
  FileText, Download, AlertTriangle, ArrowLeft, Eye, ChevronRight, ChevronLeft, ChevronDown,
  ExternalLink, Sun, Moon, ArrowUp, CheckCircle, Loader2, Share2, Award, Link as LinkIcon
} from 'lucide-react';
import Admin from './Admin';

// --- أيقونة تيك توك ---
const TikTokIcon = ({ size = 24, className, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

// --- CSS للموقع الرئيسي ---
const appStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Cairo:wght@300;400;700&family=Lato:wght@300;400;700&display=swap');
  :root { --primary: #F97316; --bg-dark: #0B0F19; --text-main: #ffffff; }
  
  .light-theme { --bg-dark: #F3F4F6; --text-main: #1F2937; }

  * { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background-color: var(--bg-dark); color: var(--text-main); font-family: 'Lato', sans-serif; overflow-x: hidden; transition: background 0.3s, color 0.3s; }
  body[dir="rtl"] { font-family: 'Cairo', sans-serif; }
  h1, h2, h3 { font-family: 'Playfair Display', serif; }
  body[dir="rtl"] h1, body[dir="rtl"] h2, body[dir="rtl"] h3 { font-family: 'Cairo', sans-serif; font-weight: 700; }

  .container { max-width: 1400px; margin: 0 auto; padding: 0 20px; position: relative; }
  
  /* Buttons & Nav */
  .btn-orange {
    background: linear-gradient(90deg, var(--primary) 0%, #EA580C 100%);
    color: white; padding: 12px 30px; border-radius: 50px; font-weight: 700;
    border: none; cursor: pointer; transition: all 0.3s ease; display: inline-flex; align-items: center; gap: 8px;
    text-decoration: none;
  }
  .btn-orange:hover { transform: translateY(-3px); box-shadow: 0 10px 20px rgba(249, 115, 22, 0.3); }
  
  .nav-link { cursor: pointer; font-weight: bold; margin: 0 15px; transition: 0.3s; text-transform: uppercase; font-size: 0.85rem; letter-spacing: 0.5px; color: inherit; text-decoration: none; }
  .nav-link:hover { color: var(--primary); }

  /* Languages */
  .lang-dropdown { position: relative; display: inline-block; margin-left: 15px; padding-bottom: 10px; margin-bottom: -10px; }
  .lang-btn {
    background: rgba(128,128,128,0.1); border: 1px solid rgba(128,128,128,0.1); 
    color: inherit; padding: 6px 12px; border-radius: 20px; cursor: pointer; 
    display: flex; align-items: center; gap: 5px; font-size: 0.75rem; font-weight: bold;
    transition: 0.3s;
  }
  .lang-btn:hover { background: rgba(128,128,128,0.2); border-color: var(--primary); }
  
  .lang-menu {
    display: none; position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
    background: var(--bg-dark); border: 1px solid rgba(128,128,128,0.1); border-radius: 10px;
    padding: 5px; min-width: 80px; z-index: 1000; box-shadow: 0 10px 30px rgba(0,0,0,0.5);
    margin-top: 5px;
  }
  .lang-menu::before { content: ''; position: absolute; top: -15px; left: 0; width: 100%; height: 20px; background: transparent; }
  .lang-dropdown:hover .lang-menu { display: block; animation: fadeIn 0.2s ease; }
  .lang-item { display: block; padding: 8px 12px; color: inherit; text-decoration: none; cursor: pointer; font-size: 0.8rem; text-align: center; border-radius: 6px; transition: 0.2s; }
  .lang-item:hover { background: var(--primary); color: white; }

  /* Hero Section */
  .hero-section { height: 100vh; position: relative; display: flex; align-items: center; justify-content: center; text-align: center; }
  .hero-overlay { position: absolute; inset: 0; background: linear-gradient(to bottom, rgba(0,0,0,0.3), var(--bg-dark)); z-index: 1; }

  /* --- Responsive Hero Typography --- */
  .hero-title {
    font-size: 4.5rem;
    margin-bottom: 20px;
    line-height: 1.1;
  }
  .hero-subtitle {
    font-size: 1.3rem;
    color: #ccc;
    margin-bottom: 40px;
    max-width: 600px;
    margin: 0 auto 40px;
  }

  /* Portfolio Slider */
  .portfolio-slider-container { margin-top: -150px; position: relative; z-index: 10; padding-bottom: 50px; }
  .slider-track { display: flex; overflow-x: auto; scroll-snap-type: x mandatory; gap: 20px; padding: 20px 40px; scrollbar-width: none; }
  
  .movie-card {
    flex: 0 0 280px; scroll-snap-align: center; aspect-ratio: 2/3;
    border-radius: 12px; overflow: hidden; position: relative; cursor: pointer;
    transition: 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94); border: 1px solid rgba(255,255,255,0.1);
    box-shadow: 0 10px 40px rgba(0,0,0,0.5);
  }
  .movie-card:hover { transform: scale(1.05) translateY(-10px); border-color: var(--primary); z-index: 5; }
  .movie-card img { width: 100%; height: 100%; object-fit: cover; }
  .card-gradient { position: absolute; inset: 0; background: linear-gradient(to top, black, transparent); opacity: 0.8; display: flex; align-items: flex-end; padding: 20px; }
  .play-btn { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) scale(0.8); opacity: 0; transition: 0.3s; background: var(--primary); width: 50px; height: 50px; border-radius: 50%; display: flex; alignItems: center; justifyContent: center; }
  .movie-card:hover .play-btn { opacity: 1; transform: translate(-50%, -50%) scale(1); }

  /* --- MODALS --- */
  .modal-backdrop {
    position: fixed; inset: 0; background: rgba(0,0,0,0.85); z-index: 9999;
    display: flex; align-items: center; justify-content: center;
    overflow-y: auto; padding: 20px; backdrop-filter: blur(8px);
    animation: fadeIn 0.3s ease-out;
  }
  .project-modal {
    width: 100%; max-width: 900px;
    background: #181818; color: #fff;
    border-radius: 10px; overflow: hidden;
    position: relative; box-shadow: 0 25px 50px rgba(0,0,0,0.8);
    animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .news-modal-container {
    width: 100%; max-width: 800px; background: #181818; border-radius: 12px;
    overflow: hidden; position: relative; box-shadow: 0 25px 50px rgba(0,0,0,0.8);
    max-height: 90vh; display: flex; flex-direction: column;
    animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    color: #fff;
  }
  .news-modal-header { position: relative; height: 300px; flex-shrink: 0; }
  .news-modal-header img { width: 100%; height: 100%; object-fit: cover; }
  .news-modal-overlay { position: absolute; inset: 0; background: linear-gradient(to top, #181818 0%, transparent 100%); }
  .news-modal-content { padding: 30px 40px; overflow-y: auto; }
  .news-tags { display: flex; gap: 10px; margin-bottom: 15px; font-size: 0.85rem; color: #ccc; }
  .news-tag { background: rgba(255,255,255,0.1); padding: 4px 10px; border-radius: 4px; }
  .news-body { font-size: 1.1rem; line-height: 1.8; color: #ddd; }
  .news-body img { max-width: 100%; height: auto; border-radius: 8px; margin: 20px 0; }

  .modal-close-btn {
    position: absolute; top: 15px; right: 15px; z-index: 20;
    width: 36px; height: 36px; border-radius: 50%;
    background: rgba(0,0,0,0.5); color: white; border: 1px solid rgba(255,255,255,0.2);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: 0.2s;
  }
  .modal-close-btn:hover { background: white; color: black; }
  body[dir="rtl"] .modal-close-btn { right: auto; left: 15px; }

  /* زر مشاركة الرابط */
  .modal-share-btn {
    position: absolute; top: 15px; right: 60px; z-index: 20;
    width: 36px; height: 36px; border-radius: 50%;
    background: rgba(0,0,0,0.5); color: white; border: 1px solid rgba(255,255,255,0.2);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: 0.2s;
  }
  .modal-share-btn:hover { background: var(--primary); border-color: var(--primary); }
  body[dir="rtl"] .modal-share-btn { right: auto; left: 60px; }


  .modal-video-wrapper { position: relative; width: 100%; padding-bottom: 56.25%; background: black; }
  .modal-video-wrapper iframe { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
  .modal-vignette { position: absolute; bottom: 0; left: 0; width: 100%; height: 30%; background: linear-gradient(to top, #181818 0%, transparent 100%); pointer-events: none; }
  .modal-content-body { padding: 30px 40px 50px; }
  .modal-title { font-size: 2.5rem; font-weight: 800; margin-bottom: 10px; line-height: 1.1; }
  .modal-meta-row { display: flex; align-items: center; gap: 15px; margin-bottom: 25px; font-size: 1rem; color: #bcbcbc; }
  .meta-year { color: #46d369; font-weight: bold; }
  .meta-badge { border: 1px solid rgba(255,255,255,0.4); padding: 2px 6px; border-radius: 4px; font-size: 0.8rem; }
  .modal-grid { display: grid; grid-template-columns: 2fr 1fr; gap: 40px; }
  .modal-desc { font-size: 1.1rem; line-height: 1.6; color: #fff; margin-bottom: 20px; }
  .modal-credits { display: flex; flex-direction: column; gap: 10px; font-size: 0.9rem; }
  
  /* --- تنسيق البوستر الطولي داخل المودال (تم إزالته من العرض ولكن أبقينا الكلاس احتياطاً) --- */
  .modal-poster { width: 100%; border-radius: 10px; margin-bottom: 20px; box-shadow: 0 10px 30px rgba(0,0,0,0.5); object-fit: cover; border: 1px solid rgba(255,255,255,0.1); }
  
  .credit-row { display: flex; flex-wrap: wrap; gap: 5px; }
  .credit-label { color: #777; }
  .credit-val { color: #ddd; }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slideUp { from { opacity: 0; transform: translateY(50px); } to { opacity: 1; transform: translateY(0); } }

  /* Other Styles */
  .contact-input { width: 100%; background: rgba(128,128,128,0.05); border: 1px solid rgba(128,128,128,0.1); padding: 15px; border-radius: 8px; color: inherit; margin-bottom: 15px; outline: none; }
  .contact-input:focus { border-color: var(--primary); }

  .floating-links { position: fixed; bottom: 30px; left: 30px; display: flex; flex-direction: column; gap: 10px; z-index: 1000; }
  .floating-link-item { 
    background: var(--text-main); color: var(--bg-dark); padding: 10px 20px; border-radius: 30px; 
    text-decoration: none; font-weight: bold; display: flex; align-items: center; gap: 8px; 
    box-shadow: 0 5px 15px rgba(0,0,0,0.3); transition: 0.3s; font-size: 0.9rem; 
  }
  .floating-link-item:hover { transform: translateX(5px); background: var(--primary); color: white; }

  .custom-section { padding: 100px 0; }
  .text-image-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; alignItems: center; }

  /* News Styles */
  .news-slider-track { display: flex; overflow-x: auto; scroll-snap-type: x mandatory; gap: 25px; padding: 20px 10px; scrollbar-width: none; }
  .news-slider-track::-webkit-scrollbar { display: none; }
  .news-card-slide {
    flex: 0 0 350px; scroll-snap-align: center;
    background: rgba(128,128,128,0.05); border-radius: 15px; overflow: hidden;
    border: 1px solid rgba(128,128,128,0.1); transition: 0.3s; cursor: pointer;
    position: relative; display: flex; flex-direction: column;
  }
  .news-card-slide:hover { transform: translateY(-5px); }
  .news-card-slide img { width: 100%; height: 200px; object-fit: cover; }
  .news-content { padding: 20px; flex: 1; display: flex; flex-direction: column; }
  .news-date { color: var(--primary); font-size: 0.8rem; font-weight: bold; margin-bottom: 8px; }
  .news-card-slide h3 { font-size: 1.2rem; margin-bottom: 10px; line-height: 1.4; }
  .news-card-slide p { font-size: 0.9rem; color: #aaa; margin: 0; }
  .nav-btn-circle {
    background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
    color: white; width: 40px; height: 40px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; transition: 0.3s;
  }
  .nav-btn-circle:hover { background: var(--primary); border-color: var(--primary); }
  @keyframes glow-pulse {
    0% { box-shadow: 0 0 0px rgba(249, 115, 22, 0); border-color: var(--primary); }
    50% { box-shadow: 0 0 20px rgba(249, 115, 22, 0.5); border-color: var(--primary); }
    100% { box-shadow: 0 0 0px rgba(249, 115, 22, 0); border-color: var(--primary); }
  }
  .newest-glow { animation: glow-pulse 2s infinite ease-in-out; border: 1px solid var(--primary) !important; }
  .new-badge {
    position: absolute; top: 15px; left: 15px; 
    background: var(--primary); color: white; padding: 4px 10px; border-radius: 4px;
    font-size: 0.7rem; font-weight: bold; z-index: 2; box-shadow: 0 2px 10px rgba(0,0,0,0.3);
  }
  body[dir="rtl"] .new-badge { left: auto; right: 15px; }

  /* --- STYLES FOR PARTNERS TICKER (شريط الشركاء الفخم) --- */
  .partners-section-container {
    padding: 30px 0 60px 0;
    overflow: hidden;
    position: relative;
    border-top: 1px solid rgba(128,128,128,0.1);
    background: linear-gradient(to bottom, var(--bg-dark), rgba(0,0,0,0.2));
  }
  
  .partners-title-small {
    text-align: center;
    font-size: 0.75rem;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #666;
    margin-bottom: 25px;
    opacity: 0.7;
  }

  /* Gradient Masks for edges */
  .partners-section-container::before,
  .partners-section-container::after {
    content: "";
    position: absolute;
    top: 0;
    width: 150px;
    height: 100%;
    z-index: 2;
    pointer-events: none;
  }
  
  .partners-section-container::before {
    left: 0;
    background: linear-gradient(to right, var(--bg-dark), transparent);
  }
  
  .partners-section-container::after {
    right: 0;
    background: linear-gradient(to left, var(--bg-dark), transparent);
  }

  .partners-ticker-track {
    display: flex;
    width: max-content;
    gap: 60px;
    animation: scrollTicker 40s linear infinite;
  }
  
  .partners-ticker-track:hover {
    animation-play-state: paused;
  }

  .partner-logo-item {
    height: 60px;
    filter: grayscale(100%) opacity(0.5);
    transition: all 0.4s ease;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .partner-logo-item img {
    height: 100%;
    width: auto;
    object-fit: contain;
    max-width: 150px;
  }

  .partner-logo-item:hover {
    filter: grayscale(0%) opacity(1);
    transform: scale(1.1);
  }

  @keyframes scrollTicker {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); } /* Scrolls half because we duplicated list */
  }

  /* --- Notification Toast Styles (تنبيهات عصرية) --- */
  .toast-container {
    position: fixed;
    top: 30px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10000;
    pointer-events: none;
  }
  
  .toast-message {
    background: rgba(20, 20, 25, 0.95);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 50px;
    padding: 12px 30px;
    display: flex;
    align-items: center;
    gap: 15px;
    box-shadow: 0 15px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05);
    animation: toastIn 0.6s cubic-bezier(0.23, 1, 0.32, 1);
    pointer-events: auto;
    min-width: 320px;
    justify-content: center;
  }
  
  .toast-icon-box {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  
  .toast-success .toast-icon-box {
    background: linear-gradient(135deg, #22c55e, #16a34a);
    box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
  }
  
  .toast-error .toast-icon-box {
    background: linear-gradient(135deg, #ef4444, #dc2626);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
  }

  .toast-text {
    font-weight: 700;
    font-size: 0.95rem;
    color: #fff;
  }

  @keyframes toastIn {
    0% { opacity: 0; transform: translateY(-30px) scale(0.9); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* Media Queries */
  @media (max-width: 992px) { .text-image-grid { grid-template-columns: 1fr; } }
  @media (max-width: 768px) {
    .movie-card { flex: 0 0 200px; }
    .news-card-slide { flex: 0 0 280px; }
    .desktop-menu { display: none !important; }
    .mobile-menu-btn { display: block !important; }
    .floating-links { bottom: 20px; left: 20px; }
    .modal-grid { grid-template-columns: 1fr; gap: 20px; }
    .modal-title { font-size: 1.8rem; }
    .modal-content-body { padding: 20px; }
    .partners-ticker-track { gap: 30px; animation-duration: 25s; }
    .partner-logo-item { height: 40px; }
    
    /* --- تصغير خط العناوين في الموبايل --- */
    .hero-title { font-size: 2.8rem; line-height: 1.2; }
    .hero-subtitle { font-size: 1rem; padding: 0 15px; }
  }
`;

// دالة مساعدة لفك ترميز العناوين المتعددة اللغات
const getLocalizedContent = (jsonString, lang) => {
  try {
    const parsed = JSON.parse(jsonString);
    return parsed[lang] || parsed['en'] || parsed['ar'] || "No Title";
  } catch (e) {
    return jsonString; // Fallback for old content
  }
};

// --- المكون الجديد: ProjectModal (النافذة الأنيقة) ---
const ProjectModal = ({ video, onClose, lang }) => {
  if (!video) return null;

  const handleBackdropClick = (e) => {
    if (e.target.className === 'modal-backdrop') onClose();
  };

  // --- دالة نسخ الرابط ---
  const copyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}?v=${video.id}`;
    navigator.clipboard.writeText(url);
    // يمكن هنا استخدام التوست بدلاً من alert إذا أردت، لكن سنبقيها بسيطة للمودال
    alert(lang === 'ar' ? 'تم نسخ رابط الفيديو' : 'Video link copied');
  };

  // --- تحديث جوهري: التحقق هل هو رابط عادي أم كود تضمين (Embed Code) ---
  const isEmbedCode = video.video_url.trim().startsWith('<'); // هل يبدأ بكود HTML؟
  let embedUrl = video.video_url;

  // إذا لم يكن كود تضمين، نعالجه كرابط عادي (يوتيوب/فيميو)
  if (!isEmbedCode) {
    if (video.video_url.includes('youtube') || video.video_url.includes('youtu.be')) {
        const vId = video.video_url.split('v=')[1]?.split('&')[0] || video.video_url.split('/').pop();
        embedUrl = `https://www.youtube.com/embed/${vId}?autoplay=1&rel=0&modestbranding=1&controls=1&showinfo=0`;
    } else if (video.video_url.includes('vimeo')) {
        const vId = video.video_url.split('/').pop();
        embedUrl = `https://player.vimeo.com/video/${vId}?autoplay=1&title=0&byline=0&portrait=0`;
    }
  }

  const title = getLocalizedContent(video.title, lang);
  const genre = getLocalizedContent(video.genre, lang); // --- قراءة التصنيف متعدد اللغات
  const isRTL = lang === 'ar';

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="project-modal">
        <button className="modal-close-btn" onClick={onClose}><X size={20} /></button>
        {/* زر المشاركة */}
        <button className="modal-share-btn" onClick={copyLink} title={isRTL ? 'نسخ الرابط' : 'Copy Link'}><LinkIcon size={18} /></button>
        
        <div className="modal-video-wrapper">
          {/* --- إذا كان كود تضمين مخصص، اعرضه مباشرة --- */}
          {isEmbedCode ? (
             <div 
               style={{position:'absolute', top:0, left:0, width:'100%', height:'100%'}} 
               dangerouslySetInnerHTML={{ __html: video.video_url }} 
             />
          ) : (
             /* --- وإلا استخدم Iframe العادي للروابط --- */
             <iframe src={embedUrl} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title={title}></iframe>
          )}
          <div className="modal-vignette"></div>
        </div>
        <div className="modal-content-body">
          <h2 className="modal-title">{title}</h2>
          <div className="modal-meta-row">
            {video.year && <span className="meta-year">{video.year}</span>}
            <span className="meta-badge">HD</span>
            {genre && <span>{genre}</span>}
          </div>
          <div className="modal-grid">
            <div className="modal-left">
              <p className="modal-desc">
                {getLocalizedContent(video.description, lang) || (isRTL ? "عمل فني مميز يعكس رؤية إبداعية فريدة. شاهد واستمتع بالتفاصيل." : "A distinctive work reflecting a unique creative vision. Watch and enjoy.")}
              </p>
            </div>
            <div className="modal-credits">
              
              {/* --- تم إزالة صورة البوستر من هنا بناءً على طلبك --- */}
              
              {video.director && (
                <div className="credit-row">
                  <span className="credit-label">{isRTL ? 'المخرج:' : 'Director:'}</span>
                  <span className="credit-val">{getLocalizedContent(video.director, lang)}</span>
                </div>
              )}
              <div className="credit-row">
                <span className="credit-label">{isRTL ? 'النوع:' : 'Genres:'}</span>
                <span className="credit-val">{genre || (isRTL ? 'عام' : 'General')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- News Modal ---
const NewsModal = ({ item, onClose, lang }) => {
  if (!item) return null;
  const isRTL = lang === 'ar';
  
  const getContent = (jsonString) => {
    try { 
        const obj = JSON.parse(jsonString);
        return obj[lang] || obj['en'] || obj['ar'] || "";
    } catch(e) { return jsonString; }
  };

  const title = getContent(item.title);
  const content = getContent(item.content);

  const handleBackdropClick = (e) => {
    if (e.target.className === 'modal-backdrop') onClose();
  };

  // --- دالة نسخ رابط الخبر ---
  const copyLink = () => {
    const url = `${window.location.origin}${window.location.pathname}?n=${item.id}`;
    navigator.clipboard.writeText(url);
    alert(lang === 'ar' ? 'تم نسخ رابط الخبر' : 'News link copied');
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="news-modal-container">
        <button className="modal-close-btn" onClick={onClose}><X size={20} /></button>
        {/* زر المشاركة */}
        <button className="modal-share-btn" onClick={copyLink} title={isRTL ? 'نسخ الرابط' : 'Copy Link'}><LinkIcon size={18} /></button>
        
        <div className="news-modal-header">
          <img src={item.image_url} alt={title} />
          <div className="news-modal-overlay"></div>
        </div>
        <div className="news-modal-content">
          <div className="news-tags">
            <span className="news-tag" style={{color: 'var(--primary)', fontWeight: 'bold'}}>{item.category || 'General'}</span>
            <span className="news-tag">{new Date(item.news_date).toLocaleDateString()}</span>
          </div>
          <h1 style={{ fontSize: '2rem', marginBottom: '20px', lineHeight: '1.3' }}>{title}</h1>
          <div className="news-body" dangerouslySetInnerHTML={{ __html: content }} />
        </div>
      </div>
    </div>
  );
};

// --- NEW COMPONENT: Partners Ticker (شريط الشركاء المتحرك) ---
const PartnersSection = ({ partners, lang }) => {
  if (!partners || partners.length === 0) return null;

  let displayList = [...partners];
  while (displayList.length < 10) {
    displayList = [...displayList, ...partners];
  }
  const infiniteList = [...displayList, ...displayList];

  return (
    <div className="partners-section-container">
      <div className="container">
        <div className="partners-title-small">
          {lang === 'ar' ? 'شركاء النجاح والجوائز' : 'Partners & Awards'}
        </div>
      </div>
      <div className="partners-ticker-track">
        {infiniteList.map((partner, index) => (
          <div key={`${partner.id}-${index}`} className="partner-logo-item" title={partner.name}>
            <img src={partner.logo_url} alt={partner.name} />
          </div>
        ))}
      </div>
    </div>
  );
};

// --- أقسام الموقع ---
const HeroSection = ({ settings, t, scrollTo }) => (
  <header id="hero" className="hero-section">
    <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${settings.hero_background_url || 'https://images.unsplash.com/photo-1485846234645-a62644f84728'})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
    <div className="hero-overlay"></div>
    <div className="container" style={{ position: 'relative', zIndex: 2 }}>
      <h1 className="hero-title">{t('heroTitle') || 'AMALCO'}</h1>
      <p className="hero-subtitle">{t('heroSubtitle')}</p>
      <button onClick={() => scrollTo('portfolio')} className="btn-orange">{t('heroPortfolioBtn') || 'Explore'} <ChevronRight/></button>
    </div>
  </header>
);

const AboutSection = ({ settings, t }) => (
  <section id="about" style={{ padding: '100px 0' }}>
    <div className="container text-image-grid">
      <div>
        <h2 style={{ fontSize: '3rem', marginBottom: 30 }}>{t('aboutTitle') || 'About Us'}</h2>
        <p style={{ fontSize: '1.1rem', opacity: 0.7, lineHeight: 1.8 }}>{t('aboutText')}</p>
        {settings.press_kit_url && <a href={settings.press_kit_url} target="_blank" rel="noreferrer" className="btn-orange" style={{ marginTop: 20, textDecoration: 'none' }}>{t('pressKitBtn')} <Download size={18}/></a>}
      </div>
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', top: -20, right: -20, width: '100%', height: '100%', border: '2px solid var(--primary)', borderRadius: 20, zIndex: 0 }}></div>
        <img src={settings.about_image_url} alt="About" style={{ width: '100%', borderRadius: 20, position: 'relative', zIndex: 1 }} />
      </div>
    </div>
  </section>
);

const PortfolioSection = ({ t, videos, trackView, setSelectedVideo, lang }) => {
  const sliderRef = useRef(null);
  const scrollSlider = (dir) => {
    if(sliderRef.current) {
      const amount = window.innerWidth > 768 ? 400 : 250;
      sliderRef.current.scrollBy({ left: dir === 'next' ? amount : -amount, behavior: 'smooth' });
    }
  };

  // --- ترجمات عنوان القسم (محدثة) ---
  const titles = {
    ar: 'أعمالنا',
    en: 'Our Work',
    fr: 'Nos Réalisations',
    de: 'Unsere Arbeit',
    es: 'Nuestro Trabajo'
  };

  // المنطق: إذا كان العنوان القادم من قاعدة البيانات هو الاسم البرمجي (portfolioTitle)
  // فهذا يعني أنه غير مترجم، لذا نستخدم القاموس الداخلي
  const rawTitle = t('portfolioTitle');
  const displayTitle = (rawTitle && rawTitle !== 'portfolioTitle') ? rawTitle : (titles[lang] || titles['en']);

  return (
    <section id="portfolio" className="portfolio-slider-container">
      <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: '2.5rem', margin: 0 }}>{displayTitle}</h2>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => scrollSlider('prev')} className="nav-btn-circle"><ChevronLeft/></button>
          <button onClick={() => scrollSlider('next')} className="nav-btn-circle"><ChevronRight/></button>
        </div>
      </div>
      
      <div className="slider-track" ref={sliderRef}>
        {videos.length > 0 ? videos.map(video => (
          <div key={video.id} className="movie-card" onClick={() => trackView('video', video.id, video, setSelectedVideo)}>
            {/* --- تعديل: إعطاء الأولوية للبوستر الطولي، ثم الصورة المصغرة، ثم يوتيوب --- */}
            <img src={video.poster_url || video.thumbnail_url || `https://img.youtube.com/vi/${video.video_url.split('v=')[1]?.split('&')[0]}/hqdefault.jpg`} alt={video.title} />
            <div className="card-gradient">
              <h4 style={{ margin: 0, fontSize: '1.1rem', textShadow: '0 2px 4px black', color: 'white' }}>{getLocalizedContent(video.title, lang)}</h4>
            </div>
            <div className="play-btn"><Play size={24} fill="white"/></div>
          </div>
        )) : <div style={{ padding: 40, color: '#666', textAlign: 'center', width: '100%' }}>No projects yet...</div>}
      </div>
    </section>
  );
};

const NewsSection = ({ t, news, trackView, setSelectedNews, lang }) => {
  const sliderRef = useRef(null);
  const scrollSlider = (dir) => {
    if(sliderRef.current) {
      const amount = window.innerWidth > 768 ? 400 : 300;
      sliderRef.current.scrollBy({ left: dir === 'next' ? amount : -amount, behavior: 'smooth' });
    }
  };
  const newestId = news.length > 0 ? news[0].id : null;

  return (
    <section id="news" style={{ padding: '80px 0', background: 'rgba(128,128,128,0.02)' }}>
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
          <h2 style={{ fontSize: '3rem', margin: 0 }}>{t('newsTitle') || 'Latest News'}</h2>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => scrollSlider('prev')} className="nav-btn-circle"><ChevronLeft/></button>
            <button onClick={() => scrollSlider('next')} className="nav-btn-circle"><ChevronRight/></button>
          </div>
        </div>
        
        <div className="news-slider-track" ref={sliderRef}>
          {news.map(item => {
            const isNewest = item.id === newestId;
            return (
              <div 
                key={item.id} 
                className={`news-card-slide ${isNewest ? 'newest-glow' : ''}`} 
                onClick={() => { trackView('news', item.id, item, setSelectedNews); }}
              >
                {isNewest && <div className="new-badge">{lang==='ar'?'جديد':'NEW'}</div>}
                <img src={item.image_url} alt={item.title} />
                <div className="news-content">
                  <div className="news-date">{new Date(item.news_date).toLocaleDateString()}</div>
                  <h3>{getLocalizedContent(item.title, lang)}</h3>
                  <p>{getLocalizedContent(item.content, lang).replace(/<[^>]*>?/gm, '').substring(0, 80)}...</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ContactSection = ({ settings, t, contactData, setContactData, sendMessage, lang }) => (
  <section id="contact" style={{ padding: '100px 0', background: settings.footer_bg_color || 'black', color: 'white' }}>
    <div className="container text-image-grid">
      <div>
        <h2 style={{ fontSize: '3rem', marginBottom: 20 }}>{t('contactTitle') || 'Contact Us'}</h2>
        <p style={{ opacity: 0.7, marginBottom: 40, fontSize: '1.2rem' }}>{t('contactText')}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', gap: 15 }}><Mail color="var(--primary)"/> {settings.contact_email}</div>
          <div style={{ display: 'flex', gap: 15 }}><Phone color="var(--primary)"/> {settings.contact_phone}</div>
          <div style={{ display: 'flex', gap: 15 }}><MapPin color="var(--primary)"/> {settings.contact_address}</div>
        </div>
      </div>
      
      <div style={{ background: 'rgba(255,255,255,0.05)', padding: 40, borderRadius: 20 }}>
        <input type="text" placeholder={lang==='ar'?'الاسم':'Name'} className="contact-input" value={contactData.name} onChange={e=>setContactData({...contactData, name:e.target.value})} />
        <input type="email" placeholder={lang==='ar'?'البريد':'Email'} className="contact-input" value={contactData.email} onChange={e=>setContactData({...contactData, email:e.target.value})} />
        <textarea rows="4" placeholder={lang==='ar'?'الرسالة...':'Message...'} className="contact-input" value={contactData.message} onChange={e=>setContactData({...contactData, message:e.target.value})}></textarea>
        <button className="btn-orange" style={{ width: '100%', justifyContent: 'center' }} onClick={sendMessage}>{t('contactBtn')} <Send size={18}/></button>
      </div>
    </div>
  </section>
);

const CustomSection = ({ data, lang }) => {
  const sectionTitle = typeof data.titles === 'object' ? (data.titles[lang] || data.titles['ar'] || data.title) : data.title;
  const sectionContent = typeof data.contents === 'object' ? (data.contents[lang] || data.contents['ar'] || data.content) : data.content;

  return (
    <section id={data.id} className="custom-section" style={{ background: 'var(--bg-dark)', padding: '100px 0' }}>
      <div className="container">
        {data.subType === 'text_only' && (
          <div style={{ textAlign: 'center', maxWidth: 800, margin: '0 auto' }}>
            <h2 style={{ fontSize: '3rem', marginBottom: 30 }}>{sectionTitle}</h2>
            <div style={{ fontSize: '1.2rem', lineHeight: 1.8, opacity: 0.8 }} dangerouslySetInnerHTML={{ __html: sectionContent }} />
          </div>
        )}
        
        {data.subType === 'text_image' && (
          <div className="text-image-grid">
            <div>
              <h2 style={{ fontSize: '3rem', marginBottom: 30 }}>{sectionTitle}</h2>
              <div style={{ fontSize: '1.1rem', opacity: 0.8, lineHeight: 1.8 }} dangerouslySetInnerHTML={{ __html: sectionContent }} />
            </div>
            <img src={data.image_url} alt={sectionTitle} style={{ width: '100%', borderRadius: 20, boxShadow: '0 10px 40px rgba(0,0,0,0.5)' }} />
          </div>
        )}

        {data.subType === 'video' && (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: '3rem', marginBottom: 40 }}>{sectionTitle}</h2>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 20, overflow: 'hidden', border: '1px solid #333', maxWidth: 1000, margin: '0 auto' }}>
              <iframe src={`https://www.youtube.com/embed/${data.video_url?.split('v=')[1]}`} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} frameBorder="0" allowFullScreen title={sectionTitle}></iframe>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};


function MainSite() {
  // --- تغيير: نبدأ بقيمة افتراضية (ar) ولكن سيتم تحديثها فوراً عند تحميل الإعدادات
  const [lang, setLang] = useState(localStorage.getItem('amalco_lang') || 'ar');
  const [settings, setSettings] = useState({ loading: true, content_json: {}, sections_config: [], quick_links_json: [], partners_json: [] });
  const [videos, setVideos] = useState([]);
  const [news, setNews] = useState([]);
  const [theme, setTheme] = useState('dark');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  
  // Contact Form
  const [contactData, setContactData] = useState({ name: '', email: '', message: '' });

  // --- Newsletter Logic (NEW) ---
  const [newsletterEmail, setNewsletterEmail] = useState('');
  
  // --- Notification Toast State (NEW) ---
  const [notification, setNotification] = useState(null); // { type: 'success' | 'error', message: '' }

  // --- إضافة useLocation للروابط المباشرة ---
  const location = useLocation();

  // Helper to show notification
  const showToast = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000); // Auto hide after 4s
  };

  // Static translations for UI elements not in DB
  const uiText = {
    ar: {
      newsTitle: 'اشترك في نشرتنا البريدية',
      newsDesc: 'كن أول من يعرف عن تجارب الأداء والأفلام الجديدة.',
      emailPlace: 'البريد الإلكتروني',
      joinBtn: 'اشتراك',
      enterEmail: 'يرجى إدخال البريد الإلكتروني',
      successSub: 'تم الاشتراك بنجاح',
      fillFields: 'يرجى تعبئة جميع الحقول',
      msgSent: 'تم الإرسال بنجاح',
      errorOccurred: 'حدث خطأ ما'
    },
    en: {
      newsTitle: 'Subscribe to our Newsletter',
      newsDesc: 'Be the first to know about casting calls and new releases.',
      emailPlace: 'Email Address',
      joinBtn: 'Join',
      enterEmail: 'Please enter email',
      successSub: 'Subscribed successfully',
      fillFields: 'Please fill all fields',
      msgSent: 'Message Sent Successfully',
      errorOccurred: 'An error occurred'
    },
    fr: {
      newsTitle: 'Abonnez-vous à notre newsletter',
      newsDesc: 'Soyez le premier informé des castings et des nouvelles sorties.',
      emailPlace: 'Adresse e-mail',
      joinBtn: 'Rejoindre',
      enterEmail: 'Veuillez entrer votre e-mail',
      successSub: 'Abonné avec succès',
      fillFields: 'Veuillez remplir tous les champs',
      msgSent: 'Message envoyé avec succès',
      errorOccurred: 'Une erreur s\'est produite'
    },
    de: {
      newsTitle: 'Abonnieren Sie unseren Newsletter',
      newsDesc: 'Erfahren Sie als Erster von Castings und Neuerscheinungen.',
      emailPlace: 'E-Mail-Adresse',
      joinBtn: 'Beitreten',
      enterEmail: 'Bitte E-Mail eingeben',
      successSub: 'Erfolgreich abonniert',
      fillFields: 'Bitte alle Felder ausfüllen',
      msgSent: 'Nachricht erfolgreich gesendet',
      errorOccurred: 'Ein Fehler ist aufgetreten'
    },
    es: {
      newsTitle: 'Suscríbete a nuestro boletín',
      newsDesc: 'Sé el primero en enterarte de los castings y los nuevos estrenos.',
      emailPlace: 'Dirección de correo electrónico',
      joinBtn: 'Unirse',
      enterEmail: 'Por favor ingrese el correo electrónico',
      successSub: 'Suscrito con éxito',
      fillFields: 'Por favor complete todos los campos',
      msgSent: 'Mensaje enviado con éxito',
      errorOccurred: 'Ocurrió un error'
    }
  };

  const txt = uiText[lang] || uiText['en'];

  const handleSubscribe = async () => {
    if(!newsletterEmail) return showToast(txt.enterEmail, 'error');
    try {
      const res = await fetch('/.netlify/functions/newsletter', {
        method: 'POST',
        body: JSON.stringify({ email: newsletterEmail })
      });
      if(res.ok) {
        showToast(txt.successSub, 'success');
        setNewsletterEmail('');
      } else {
        showToast(txt.errorOccurred, 'error');
      }
    } catch(e) { console.error(e); }
  };

  // --- دالة مساعدة لتغيير اللغة وحفظها في المتصفح ---
  const changeLanguage = (newLang) => {
    setLang(newLang);
    localStorage.setItem('amalco_lang', newLang);
  };

  useEffect(() => {
    fetch('/.netlify/functions/recordVisit', { method: 'POST' });
    const fetchData = async () => {
      try {
        const [s, v, n] = await Promise.all([
          fetch('/.netlify/functions/getSettings').then(r => r.json()),
          fetch('/.netlify/functions/getVideos').then(r => r.json()),
          fetch('/.netlify/functions/getNews').then(r => r.json())
        ]);

        const parse = (val, def) => {
          if (typeof val === 'string') {
            try { return JSON.parse(val); } catch(e) { return def; }
          }
          return val || def;
        };

        const DEFAULT_SECTIONS = [
          { id: 'hero', type: 'core', visible: true },
          { id: 'about', type: 'core', visible: true },
          { id: 'portfolio', type: 'core', visible: true },
          { id: 'news', type: 'core', visible: true },
          { id: 'contact', type: 'core', visible: true }
        ];

        let sections = parse(s.sections_config, null);
        if (!Array.isArray(sections)) sections = DEFAULT_SECTIONS;

        const processedSettings = {
          ...s,
          content_json: parse(s.content_json, {}),
          sections_config: sections,
          quick_links_json: parse(s.quick_links_json, []),
          partners_json: parse(s.partners_json, []), 
          loading: false
        };

        // --- المنطق الذكي لاختيار اللغة ---
        const savedLang = localStorage.getItem('amalco_lang');
        if (savedLang) {
          // إذا كان الزائر قد اختار لغة سابقاً، نحترم خياره
          setLang(savedLang);
        } else {
          // إذا كان زائراً جديداً، نستخدم اللغة الافتراضية من الإعدادات
          // إذا لم تكن موجودة، نستخدم الإنجليزية كاحتياط
          setLang(processedSettings.default_language || 'en');
        }

        setSettings(processedSettings);
        setVideos(v);
        setNews(n);
        setTheme(s.default_theme || 'dark');

        // --- منطق فتح الروابط المباشرة (Deep Linking) ---
        const params = new URLSearchParams(window.location.search);
        const videoId = params.get('v');
        const newsId = params.get('n');

        if (videoId && v) {
            const targetVideo = v.find(item => item.id.toString() === videoId.toString());
            if (targetVideo) setSelectedVideo(targetVideo);
        }
        if (newsId && n) {
            const targetNews = n.find(item => item.id.toString() === newsId.toString());
            if (targetNews) setSelectedNews(targetNews);
        }

      } catch (e) { console.error(e); setSettings({loading:false}); }
    };
    fetchData();
  }, [location.search]); // --- إضافة location.search للاعتماديات

  useEffect(() => {
    document.body.dir = lang === 'ar' ? 'rtl' : 'ltr';
    document.body.className = theme === 'light' ? 'light-theme' : '';
    
    if(settings.primary_color) document.documentElement.style.setProperty('--primary', settings.primary_color);
    
    if(settings.seo_title) document.title = settings.seo_title;
    if(settings.favicon_url) {
      const existingLink = document.querySelector("link[rel*='icon']");
      if (existingLink) { existingLink.href = settings.favicon_url; } 
      else { const link = document.createElement('link'); link.rel = 'icon'; link.href = settings.favicon_url; document.head.appendChild(link); }
    }
  }, [lang, settings, theme]);

  const t = (key) => settings.content_json?.[lang]?.[key] || key;
  const scrollTo = (id) => { setMobileMenu(false); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); };

  const trackView = (type, id, item = null, setter = null) => {
    if (setter && item) { setter(item); }
    fetch('/.netlify/functions/incrementView', { method: 'POST', body: JSON.stringify({ type, id }) });
  };

  const sendMessage = async () => {
    if(!contactData.name || !contactData.email || !contactData.message) return showToast(txt.fillFields, 'error');
    const res = await fetch('/.netlify/functions/sendMessage', { method: 'POST', body: JSON.stringify(contactData) });
    if(res.ok) { 
        showToast(txt.msgSent, 'success');
        setContactData({name:'', email:'', message:''}); 
    } else {
        showToast(txt.errorOccurred, 'error');
    }
  };

  if(settings.loading) return <div style={{height:'100vh', display:'flex', alignItems:'center', justifyContent:'center'}}>Amalco Film Production</div>;

  return (
    <div style={{ backgroundColor: 'var(--bg-dark)', color: 'var(--text-main)' }}>
      <style>{appStyles}</style>
      
      {/* Notification Toast Render */}
      {notification && (
          <div className="toast-container">
              <div className={`toast-message ${notification.type === 'success' ? 'toast-success' : 'toast-error'}`}>
                  <div className="toast-icon-box">
                      {notification.type === 'success' ? <CheckCircle size={18} color="white" strokeWidth={3} /> : <XCircle size={18} color="white" strokeWidth={3} />}
                  </div>
                  <span className="toast-text">{notification.message}</span>
              </div>
          </div>
      )}
      
      {selectedVideo && <ProjectModal video={selectedVideo} onClose={()=>setSelectedVideo(null)} lang={lang} />}
      {selectedNews && <NewsModal item={selectedNews} onClose={()=>setSelectedNews(null)} lang={lang} />}

      <div className="floating-links">
        {Array.isArray(settings.quick_links_json) && settings.quick_links_json.filter(l => l.type === 'floating').map(link => (
          <a key={link.id} href={link.url} target="_blank" rel="noreferrer" className="floating-link-item">
            {link.title} <ExternalLink size={14}/>
          </a>
        ))}
      </div>

      {/* Navbar */}
      <nav style={{ position: 'fixed', top: 0, width: '100%', zIndex: 100, padding: '15px 0', background: 'linear-gradient(to bottom, rgba(0,0,0,0.9), transparent)', backdropFilter: 'blur(5px)' }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor:'pointer' }} onClick={()=>window.scrollTo(0,0)}>
            {settings.logo_url ? <img src={settings.logo_url} style={{height: 35, objectFit:'contain'}} alt="Logo" /> : <h2 style={{fontSize:'1.5rem', fontWeight:'bold', letterSpacing:'1px'}}>AMALCO</h2>}
          </div>
          
          <div className="desktop-menu" style={{ display: 'flex', alignItems: 'center' }}>
            {settings.sections_config.map(section => (
              section.visible && (section.type === 'core' || section.type === 'custom') && 
              <span key={section.id} onClick={() => scrollTo(section.id)} className="nav-link">
                {section.type === 'custom' 
                  ? (section.titles?.[lang] || section.title) 
                  : (t(`menu.${section.id}`) || section.title)}
              </span>
            ))}
            {Array.isArray(settings.quick_links_json) && settings.quick_links_json.filter(l => l.type === 'header').map(link => (
               <a key={link.id} href={link.url} target="_blank" rel="noreferrer" className="nav-link" style={{color:'var(--primary)'}}>{link.title}</a>
            ))}
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', margin: '0 15px' }}>
              {theme === 'dark' ? <Sun size={20}/> : <Moon size={20}/>}
            </button>
            <div className="lang-dropdown">
              <div className="lang-btn">
                <Globe size={14} /> {lang.toUpperCase()} <ChevronDown size={14} />
              </div>
              <div className="lang-menu">
                {['ar', 'en', 'fr', 'de', 'es'].map(l => (
                  <div key={l} className="lang-item" onClick={() => changeLanguage(l)}>{l.toUpperCase()}</div>
                ))}
              </div>
            </div>
          </div>
          <button className="mobile-menu-btn" style={{ background: 'none', border: 'none', color: 'inherit', display: 'none' }} onClick={() => setMobileMenu(!mobileMenu)}>
            <Menu size={28} />
          </button>
        </div>
      </nav>

      {mobileMenu && <div style={{ position: 'fixed', inset: 0, background: 'var(--bg-dark)', zIndex: 90, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 30 }}>
        {settings.sections_config.map(section => (
          section.visible && <span key={section.id} onClick={() => scrollTo(section.id)} style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
             {section.type === 'custom' ? (section.titles?.[lang] || section.title) : (t(`menu.${section.id}`) || section.title)}
          </span>
        ))}
        <button onClick={()=>setMobileMenu(false)} style={{position:'absolute', top:30, right:30, background:'none', border:'none', color:'inherit'}}><X size={30}/></button>
      </div>}

      {/* Main Sections Loop */}
      {settings.sections_config.map(section => {
        if (!section.visible) return null;
        if (section.type === 'core') {
          switch(section.id) {
            case 'hero': return <HeroSection key={section.id} settings={settings} t={t} scrollTo={scrollTo} />;
            case 'about': return <AboutSection key={section.id} settings={settings} t={t} />;
            case 'portfolio': return <PortfolioSection key={section.id} t={t} videos={videos} trackView={trackView} setSelectedVideo={setSelectedVideo} lang={lang} />;
            case 'news': return <NewsSection key={section.id} t={t} news={news} trackView={trackView} setSelectedNews={setSelectedNews} lang={lang} />;
            case 'contact': return <ContactSection key={section.id} settings={settings} t={t} contactData={contactData} setContactData={setContactData} sendMessage={sendMessage} lang={lang} />;
            default: return null;
          }
        } else if (section.type === 'custom') {
          return <CustomSection key={section.id} data={section} lang={lang} />;
        }
        return null;
      })}

      {/* --- Partners Section Placed Before Footer --- */}
      <PartnersSection partners={settings.partners_json} lang={lang} />

      <footer style={{ textAlign: 'center', padding: '20px', borderTop: 'none', background: settings.footer_bg_color || 'black', marginTop: 0 }}>
        
        {/* --- Newsletter Section (NEW) --- */}
        <div style={{ padding: '30px 20px', marginBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div className="container" style={{ maxWidth: 600 }}>
            <h3 style={{ marginBottom: 15, fontSize: '1.5rem' }}>{txt.newsTitle}</h3>
            <p style={{ color: '#aaa', marginBottom: 20 }}>{txt.newsDesc}</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <input 
                type="email" 
                placeholder={txt.emailPlace} 
                style={{ flex: 1, padding: '12px', borderRadius: '30px', border: '1px solid #333', background: 'rgba(255,255,255,0.05)', color: 'white', outline: 'none' }}
                value={newsletterEmail}
                onChange={e => setNewsletterEmail(e.target.value)}
              />
              <button className="btn-orange" style={{ padding: '10px 25px', borderRadius: '30px' }} onClick={handleSubscribe}>
                {txt.joinBtn}
              </button>
            </div>
          </div>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginBottom: 30, paddingTop: 10 }}>
          {settings.social_facebook && <a href={settings.social_facebook} target="_blank" rel="noreferrer" style={{color:'#888'}}><Facebook/></a>}
          {settings.social_instagram && <a href={settings.social_instagram} target="_blank" rel="noreferrer" style={{color:'#888'}}><Instagram/></a>}
          {settings.social_twitter && <a href={settings.social_twitter} target="_blank" rel="noreferrer" style={{color:'#888'}}><Twitter/></a>}
          {settings.social_youtube && <a href={settings.social_youtube} target="_blank" rel="noreferrer" style={{color:'#888'}}><Youtube/></a>}
          {settings.social_tiktok && <a href={settings.social_tiktok} target="_blank" rel="noreferrer" style={{color:'#888'}}><TikTokIcon/></a>}
          {settings.social_linkedin && <a href={settings.social_linkedin} target="_blank" rel="noreferrer" style={{color:'#888'}}><Linkedin/></a>}
        </div>
        <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'center', gap: 15, flexWrap: 'wrap' }}>
          {Array.isArray(settings.quick_links_json) && settings.quick_links_json.filter(l => l.type === 'footer').map(link => (
            <a key={link.id} href={link.url} target="_blank" rel="noreferrer" style={{color:'var(--primary)', textDecoration:'none', fontSize:'0.8rem'}}>{link.title}</a>
          ))}
        </div>
        <p style={{ color: '#555', fontSize: '0.8rem' }}>{settings.copyright_text || '© 2026 AMALCO. All Rights Reserved.'}</p>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<MainSite />} />
        <Route path="/admin-amalco" element={<Admin />} />
        <Route path="*" element={
          <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0B0F19', color: 'white' }}>
            <h1 style={{ fontSize: '6rem', color: '#F97316' }}>404</h1>
            <Link to="/" style={{ color: 'white', textDecoration: 'underline' }}>Return Home</Link>
          </div>
        } />
      </Routes>
    </Router>
  );
}

export default App;